import { useRef, useState } from "react";
import { useNavigate, useSearchParams, useOutletContext } from "react-router-dom";
import {
  X,
  ImagePlus,
  MessageSquare,
  MessageCircle,
  Loader2,
} from "lucide-react";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import UserAvatar from "@/components/UserAvatar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Compose — 1:1 web port of iOS `src/app/(app)/compose.tsx`.
 *
 * Two post types:
 *   • Standard  — text + EXACTLY one picture (backend rule).  The picture is
 *                 compressed on-device (max 1440px, JPEG ~75%) then sent as
 *                 a raw binary inside the create-post multipart FormData.
 *   • Opinion   — text-only view statement.  Up to 4 optional images/videos
 *                 are PRE-uploaded to `/media/upload` immediately after they
 *                 are picked, and only their `media_ids` are sent when the
 *                 user finally publishes.
 *
 * Every submission passes the backend's content moderation.  If flagged the
 * user stays on the composer and sees the red "Content flagged" banner
 * instead of navigating away.
 *
 * Rendered as a modal-style page routed at `/create`; the AppShell's centre
 * ⊕ tab pushes here.
 */

const MAX_CHARS = 500;
const MAX_MEDIA = 4;

const BRONTO_RAINBOW =
  "linear-gradient(90deg, #FFD600, #FF7A00, #FF0069, #D300C5, #7638FA)";
const BRAND = "#7638FA";

// ------------------------------------------------------------------
// Client-side moderation helpers — mirror `mobile/src/lib/xano/moderation.ts`.
// ------------------------------------------------------------------
function isModerationFlagged(payload) {
  if (!payload || typeof payload !== "object") return false;
  const flags = [
    payload.moderation_flagged,
    payload.flagged,
    payload.is_flagged,
    payload.data?.moderation_flagged,
    payload.data?.flagged,
    payload.data?.is_flagged,
  ];
  return flags.some((f) => f === true);
}
function isModerationError(e) {
  const status = e?.response?.status;
  const code = (e?.response?.data?.code || e?.response?.data?.error || "").toString().toLowerCase();
  const msg = (e?.response?.data?.message || e?.response?.data?.error || "").toString().toLowerCase();
  if (status === 422 || status === 451) return true;
  return code.includes("moderation") || code.includes("flag") ||
         msg.includes("moderation") || msg.includes("flagged");
}

// ------------------------------------------------------------------
// Compress a picked File to a Blob at max width 1440px + JPEG q≈0.75.
// Same target as iOS `ImageManipulator.manipulateAsync` in the spec.
// ------------------------------------------------------------------
function compressImageFile(file) {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/")) return resolve(file);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const ratio = Math.min(1, 1440 / img.width);
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return resolve(file);
          const name = file.name.replace(/\.\w+$/, "") + ".jpg";
          const out = new File([blob], name, { type: "image/jpeg" });
          resolve(out);
        },
        "image/jpeg",
        0.75
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}

function ModerationWarning({ testId }) {
  return (
    <div
      className="rounded-2xl p-4 flex items-start gap-3"
      style={{
        borderWidth: 1,
        borderColor: "rgba(255, 59, 48, 0.35)",
        backgroundColor: "rgba(255, 59, 48, 0.10)",
      }}
      data-testid={testId}
    >
      <div className="w-9 h-9 rounded-full bg-[#FF3B30] flex items-center justify-center shrink-0">
        <X size={16} className="text-white" strokeWidth={3} />
      </div>
      <div>
        <div className="text-[#FF3B30] font-bold text-[14px]">
          Content flagged
        </div>
        <p className="text-[13px] text-foreground/85 leading-5 mt-0.5">
          Your post can&apos;t be published as it stands. Please review your
          content against BrontoY&apos;s Community Guidelines and try again.
        </p>
      </div>
    </div>
  );
}

function TypeCard({ active, onClick, Icon, title, subtitle, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex-1 min-h-[68px] rounded-2xl border-[1.5px] p-3 text-left transition-all bg-card ${
        active ? "" : "border-border hover:opacity-90 active:opacity-70"
      }`}
      style={active ? { borderColor: "transparent" } : {}}
      data-testid={testId}
    >
      {active && (
        <span
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{ background: BRONTO_RAINBOW, padding: "2px" }}
        >
          <span className="block w-full h-full rounded-[14px] bg-card" />
        </span>
      )}
      <span className="relative flex items-center gap-1.5">
        <Icon
          size={14}
          strokeWidth={2}
          className={active ? "text-foreground" : "text-muted-foreground"}
        />
        <span className="text-foreground text-[13px] font-extrabold">
          {title}
        </span>
      </span>
      <span
        className="relative block text-muted-foreground mt-1 truncate"
        style={{ fontSize: 10.5 }}
      >
        {subtitle}
      </span>
    </button>
  );
}

function MediaPreview({ entry, onRemove, index }) {
  return (
    <div
      className="relative rounded-[18px] overflow-hidden bg-secondary border border-border"
      style={{ width: "48%", aspectRatio: "1 / 1" }}
      data-testid={`compose-media-${index}`}
    >
      {entry.isVideo ? (
        <video
          src={entry.previewUrl}
          className="w-full h-full object-cover"
          muted
          loop
          playsInline
        />
      ) : (
        <img
          src={entry.previewUrl}
          alt=""
          className="w-full h-full object-cover"
          draggable={false}
        />
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1.5 right-1.5 w-[26px] h-[26px] rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
        aria-label="Remove"
        data-testid={`compose-media-remove-${index}`}
      >
        <X size={14} className="text-white" />
      </button>
      {entry.uploading && (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
      )}
      {entry.error && (
        <div className="absolute inset-0 bg-[rgba(255,59,48,0.7)] flex items-center justify-center p-2">
          <p className="text-white text-[11px] text-center">{entry.error}</p>
        </div>
      )}
    </div>
  );
}

export default function Compose() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const outlet = useOutletContext() || {};
  const { user: userFromCtx } = useAuth();
  const user = outlet.user || userFromCtx;

  const [type, setType] = useState(params.get("type") === "opinion" ? "opinion" : "standard");
  const [content, setContent] = useState("");
  const [media, setMedia] = useState([]);
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState("");
  const [flagged, setFlagged] = useState(false);

  const fileInputRef = useRef(null);

  const isOpinion = type === "opinion";
  const isStandard = type === "standard";
  const remaining = MAX_CHARS - content.length;

  // Standard posts require EXACTLY one attached picture (backend rule).
  const canPost = isStandard
    ? content.trim().length > 0 &&
      remaining >= 0 &&
      !posting &&
      media.length === 1 &&
      !media[0].uploading &&
      !media[0].error
    : content.trim().length > 0 &&
      remaining >= 0 &&
      !posting &&
      !media.some((m) => m.uploading);

  const setEntry = (uri, patch) => {
    setMedia((prev) => prev.map((m) => (m.uri === uri ? { ...m, ...patch } : m)));
  };

  // Standard-post pictures are shrunk to a sane upload size (max 1440px,
  // JPEG ~75%) as soon as they're picked — a full-res phone photo is
  // several MB and the upload dominates how long "Post" takes.
  const compressOne = async (entry) => {
    try {
      const compressed = await compressImageFile(entry.file);
      setEntry(entry.uri, {
        uploading: false,
        uploadFile: compressed,
      });
    } catch {
      setEntry(entry.uri, { uploading: false });
    }
  };

  // Opinion attachments go through the /media pre-upload; the returned
  // media_id is stashed and later included in the create-post request.
  const uploadOne = async (entry) => {
    try {
      const fd = new FormData();
      fd.append("file", entry.file, entry.file.name);
      if (entry.durationSec > 0) fd.append("duration", String(entry.durationSec));
      const res = await api.post(EP.mediaUpload, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const p = res.data;
      const mid = p?.data?.id ?? p?.id ?? p?.media_id ?? p?.data?.media_id;
      setEntry(entry.uri, { uploading: false, media_id: mid });
    } catch (e) {
      setEntry(entry.uri, { uploading: false, error: apiError(e) });
    }
  };

  const ingestFiles = async (fileList) => {
    const cap = isStandard ? 1 : MAX_MEDIA;
    const remainingSlots = cap - media.length;
    if (remainingSlots <= 0) return;
    const files = Array.from(fileList).slice(0, remainingSlots);

    // Get video duration where applicable — mirrors iOS `a.duration`.
    const entries = await Promise.all(
      files.map(async (file) => {
        const isVideo = file.type.startsWith("video/");
        const previewUrl = URL.createObjectURL(file);
        let durationSec = 0;
        if (isVideo) {
          durationSec = await new Promise((resolve) => {
            const v = document.createElement("video");
            v.preload = "metadata";
            v.onloadedmetadata = () => {
              resolve(Math.max(1, Math.round(v.duration || 1)));
              URL.revokeObjectURL(v.src);
            };
            v.onerror = () => resolve(1);
            v.src = URL.createObjectURL(file);
          });
        }
        return {
          uri: `local:${file.name}:${file.size}:${file.lastModified}`,
          file,
          previewUrl,
          isVideo,
          durationSec,
          uploading: true,
          media_id: null,
          error: null,
        };
      })
    );

    setMedia((prev) => [...prev, ...entries]);
    if (isStandard) entries.forEach(compressOne);
    else entries.forEach(uploadOne);
  };

  const onFileInput = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    ingestFiles(files);
    e.target.value = ""; // allow re-picking the same file
  };

  const removeMedia = (uri) => {
    setMedia((prev) => {
      const dropping = prev.find((m) => m.uri === uri);
      if (dropping) URL.revokeObjectURL(dropping.previewUrl);
      return prev.filter((m) => m.uri !== uri);
    });
  };

  const switchType = (nextType) => {
    if (type === nextType) return;
    // Clear media on type switch — Standard/Opinion require different upload paths.
    media.forEach((m) => URL.revokeObjectURL(m.previewUrl));
    setMedia([]);
    setType(nextType);
    setFlagged(false);
  };

  const publish = async () => {
    if (!canPost) return;
    setPosting(true);
    setErr("");
    setFlagged(false);
    try {
      let res;
      if (isStandard) {
        // Standard: multipart with `image` file binary.
        const img = media[0];
        const uploadFile = img.uploadFile || img.file;
        const fd = new FormData();
        fd.append("image", uploadFile, uploadFile.name);
        fd.append("content", content.trim());
        fd.append("type", "post");
        fd.append("Post_type", "Standard");
        res = await api.post(EP.createPost, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        // Opinion: JSON, image explicitly null, media_ids attached.
        const media_ids = media.filter((m) => m.media_id).map((m) => m.media_id);
        res = await api.post(EP.createPost, {
          content: content.trim(),
          type: "post",
          Post_type: "Opinion",
          image: null,
          media_ids,
        });
      }
      const payload = unwrap(res.data);
      if (isModerationFlagged(payload) || isModerationFlagged(payload?.post)) {
        setFlagged(true);
        return;
      }
      toast.success("Posted");
      // Note: we can't invalidate react-query keys without setup — feed
      // components refetch on mount, so returning back is enough.
      if (window.history.length > 1) navigate(-1);
      else navigate("/home", { replace: true });
    } catch (e) {
      if (isModerationError(e)) setFlagged(true);
      else setErr(apiError(e));
    } finally {
      setPosting(false);
    }
  };

  const remainingClass =
    remaining < 0
      ? "text-[#FF3B30]"
      : remaining < 50
      ? "text-amber-500"
      : "text-muted-foreground";

  return (
    <div
      className="min-h-[100dvh] w-full bg-background text-foreground flex flex-col relative"
      data-testid="compose-screen"
    >
      {/* Header — X close · "Create" · rainbow Post pill */}
      <div
        className="h-[52px] px-3 flex items-center justify-between border-b border-border shrink-0"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/home"))}
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary active:opacity-85 transition-all"
          aria-label="Discard"
          data-testid="compose-discard"
        >
          <X size={22} className="text-foreground" />
        </button>
        <h1
          className="font-jakarta font-bold text-foreground"
          style={{ fontSize: 17, letterSpacing: "-0.3px" }}
        >
          Create
        </h1>
        <button
          type="button"
          onClick={publish}
          disabled={!canPost}
          className="min-w-[68px] h-9 rounded-full flex items-center justify-center px-4 transition-opacity active:opacity-85"
          style={{
            background: BRONTO_RAINBOW,
            opacity: !canPost ? 0.4 : 1,
            cursor: !canPost ? "not-allowed" : "pointer",
          }}
          data-testid="compose-publish"
        >
          {posting ? (
            <Loader2 className="w-4 h-4 animate-spin text-white" />
          ) : (
            <span className="text-white font-extrabold text-[14px]">Post</span>
          )}
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 pb-11">
        <div className="mx-auto w-full max-w-[560px]">
          {/* Type segmented cards */}
          <div className="flex gap-2.5 mb-4">
            <TypeCard
              active={isStandard}
              onClick={() => switchType("standard")}
              Icon={MessageSquare}
              title="Standard"
              subtitle="Text, images & clips"
              testId="compose-type-standard"
            />
            <TypeCard
              active={isOpinion}
              onClick={() => switchType("opinion")}
              Icon={MessageCircle}
              title="Opinion"
              subtitle="Agree · Different View"
              testId="compose-type-opinion"
            />
          </div>

          {/* Composer card */}
          <div
            className="rounded-[22px] p-4 bg-card"
            style={{
              borderWidth: 1,
              borderColor: isOpinion ? BRAND : "hsl(var(--border))",
            }}
          >
            <div className="flex gap-3">
              <UserAvatar user={user} size="md" />
              <textarea
                autoFocus
                value={content}
                onChange={(e) => {
                  setContent(e.target.value);
                  if (flagged) setFlagged(false);
                }}
                maxLength={MAX_CHARS + 100}
                placeholder={
                  isOpinion ? "State your view. Be clear and specific." : "What's happening?"
                }
                className="flex-1 bg-transparent text-foreground text-[17px] leading-6 focus:outline-none resize-none"
                style={{ minHeight: 120, paddingTop: 6 }}
                data-testid="compose-textarea"
              />
            </div>

            {/* Standard posts need exactly one picture — tappable hint */}
            {isStandard && media.length === 0 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-3.5 rounded-2xl px-4 py-3.5 bg-secondary hover:opacity-85 active:opacity-70 transition-opacity mt-3.5 w-full text-left"
                style={{ borderWidth: 1.5, borderColor: "hsl(var(--border))" }}
                data-testid="compose-photo-required"
              >
                <ImagePlus size={22} strokeWidth={1.8} style={{ color: BRAND }} />
                <div className="flex-1">
                  <div className="text-foreground text-[14.5px] font-bold">
                    Add a photo
                  </div>
                  <div className="text-muted-foreground text-[12px] mt-0.5">
                    Standard posts include exactly one picture
                  </div>
                </div>
              </button>
            )}

            {/* Media previews */}
            {media.length > 0 && (
              <div className="flex flex-wrap gap-2.5 mt-4">
                {media.map((m, i) => (
                  <MediaPreview
                    key={m.uri}
                    entry={m}
                    index={i}
                    onRemove={() => removeMedia(m.uri)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Moderation / error banners */}
          {flagged ? (
            <div className="mt-3.5">
              <ModerationWarning testId="compose-moderation-warning" />
            </div>
          ) : err ? (
            <div
              className="rounded-2xl p-3.5 mt-3.5"
              style={{ backgroundColor: "rgba(255, 59, 48, 0.13)" }}
              data-testid="compose-error"
            >
              <p className="text-[#FF3B30] text-sm">{err}</p>
            </div>
          ) : null}
        </div>
      </div>

      {/* Bottom action bar */}
      <div
        className="flex items-center px-4 h-[52px] border-t border-border shrink-0"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={media.length >= (isStandard ? 1 : MAX_MEDIA)}
          className="w-10 h-10 flex items-center justify-center hover:opacity-70 transition-opacity"
          style={{ opacity: media.length >= (isStandard ? 1 : MAX_MEDIA) ? 0.4 : 1 }}
          aria-label="Add media"
          data-testid="compose-add-media"
        >
          <ImagePlus size={22} strokeWidth={1.8} style={{ color: BRAND }} />
        </button>
        <div className="flex-1" />
        <span
          className={`text-[13px] font-bold ${remainingClass}`}
          style={{ fontVariantNumeric: "tabular-nums" }}
          data-testid="compose-char-count"
        >
          {remaining}
        </span>
      </div>

      {/* Hidden file input driving both the header and hint pickers */}
      <input
        ref={fileInputRef}
        type="file"
        accept={isStandard ? "image/*" : "image/*,video/*"}
        multiple={!isStandard}
        onChange={onFileInput}
        className="hidden"
        data-testid="compose-file-input"
      />

      {/* Posting overlay — a big image upload can take a few seconds */}
      {posting && (
        <div
          className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center px-6"
          data-testid="compose-posting-overlay"
        >
          <div className="rounded-[22px] bg-card px-8 py-7 flex flex-col items-center gap-3 shadow-2xl">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: BRAND }} />
            <p className="text-foreground font-bold text-[15px]">Posting…</p>
            <p className="text-muted-foreground text-[12.5px] text-center max-w-[220px]">
              {isStandard
                ? "Uploading your photo. This can take a moment."
                : "Sharing your opinion."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
